import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";

// Backend constants — must match server.py (LOYALTY_EARN_RATE_PER_DOLLAR / LOYALTY_POINTS_PER_DOLLAR).
const EARN_RATE_PER_DOLLAR = 5;   // 5 pts per $1 spent
const POINTS_PER_DOLLAR = 100;    // 100 pts = $1 at redeem

/**
 * Loyalty recap block — used on `/order/:num` (success screen) AND on the
 * `/pay/:num` payment page so buyers see exactly how many points they saved
 * with and how many they'll earn the moment the order is placed. The earn
 * projection mirrors award_loyalty_points() so what we show equals what will
 * later land on the ledger once the order settles.
 */
export function LoyaltyRecap({ order }) {
  if (!order) return null;

  const subtotal = Number(order.subtotal_usd || 0);
  const discount = Number(order.discount_usd || 0);
  const paidForEarn = Math.max(0, Math.floor(subtotal - discount));
  const projectedEarnPts = paidForEarn * EARN_RATE_PER_DOLLAR;
  const projectedEarnUsd = projectedEarnPts / POINTS_PER_DOLLAR;
  const pointsRedeemed = Number(order.loyalty_points_used || 0);
  const savedFromPoints = Number(order.loyalty_discount_usd || 0);

  // Nothing to show if the order didn't touch the points program.
  if (pointsRedeemed === 0 && projectedEarnPts === 0) return null;

  return (
    <div
      className="border border-[var(--terra)]/40 bg-[var(--terra)]/5 rounded-md p-5"
      data-testid="loyalty-recap"
    >
      <div className="flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.22em] text-[var(--terra)]">
        <Sparkles className="h-3.5 w-3.5" /> Loyalty program
      </div>
      <div className="mt-3 space-y-2.5 text-sm">
        {pointsRedeemed > 0 && (
          <div className="flex items-baseline justify-between gap-4" data-testid="loyalty-recap-saved">
            <span className="text-[var(--ink-soft)]">You saved</span>
            <span className="font-mono">
              <span className="text-[var(--olive)] font-semibold">${savedFromPoints.toFixed(2)}</span>
              <span className="text-[var(--ink-muted)] text-[12px]"> ({pointsRedeemed.toLocaleString()} pts redeemed)</span>
            </span>
          </div>
        )}
        {projectedEarnPts > 0 && (
          <div className="flex items-baseline justify-between gap-4" data-testid="loyalty-recap-earn">
            <span className="text-[var(--ink-soft)]">You'll earn</span>
            <span className="font-mono">
              <span className="text-[var(--terra)] font-semibold">{projectedEarnPts.toLocaleString()} pts</span>
              <span className="text-[var(--ink-muted)] text-[12px]"> (≈ ${projectedEarnUsd.toFixed(2)} off next order)</span>
            </span>
          </div>
        )}
      </div>
      <p className="mt-3 text-[11.5px] text-[var(--ink-muted)] leading-relaxed">
        Earned points post to your balance once the order is approved. Points expire 6 months after they land — <Link to="/account/points" className="underline underline-offset-2 hover:text-[var(--terra)]">view your balance</Link>.
      </p>
    </div>
  );
}
